import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDate } from '../../../lib/utils';
import { X, AlertTriangle, MapPin, Calendar, ListOrdered, Users } from 'lucide-react';
import AssignCourtsDialog from './AssignCourtsDialog';
import { getCustomerInfo } from './utils';
import CustomerInfoInline from '../components/CustomerInfoInline';
import { formatTimeRange } from '../BookingSeries/seriesHelpers';

/**
 * Compact pending-booking card — 3 rows + optional notes row.
 * Replaces the previous tall multi-section layout.
 */
export default function PendingBookingCard({
  booking, location, cancelling,
  onCancelBooking,
  isDialogOpen, setIsDialogOpen, selectedBooking,
  courtsAssigned, availableCourts, assignedCourtNumbers,
  getAvailableCourtNumbersForDropdown, onUpdateCourt,
  discountType, setDiscountType,
  discountValue, setDiscountValue,
  discountReason, setDiscountReason,
  onAssignClick, onConfirmAssign,
}) {
  const customer = getCustomerInfo(booking);
  const refShort = booking.id.slice(0, 8);

  return (
    <Card className="px-4 py-3 space-y-2" data-testid="pending-booking-card">
      {/* Row 1 — header pills + total */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
            #{refShort}
          </span>
          {booking.source === 'WAITLIST_CONVERSION' && (
            <span
              data-testid={`from-waitlist-badge-${booking.id}`}
              title={booking.request_created_at
                ? `Waitlisted on ${formatDate(booking.request_created_at)}`
                : 'Promoted from waitlist'}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
            >
              <ListOrdered className="w-3 h-3" /> From Waitlist
            </span>
          )}
          {booking.from_waitlist && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200"
              title={`Auto-promoted on ${formatDate(booking.waitlist_promoted_at || booking.created_at)}`}
            >
              <Users className="w-3 h-3" /> Priority
            </span>
          )}
          {booking.duration && booking.duration > 1 && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground">
              {booking.duration}h
            </span>
          )}
        </div>
        <div className="text-right">
          <span className="text-xs text-muted-foreground">Est. Total</span>{' '}
          <span className="font-bold text-lg">${(booking.estimated_total ?? 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Row 2 — date/time · location · customer */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-1 text-sm border-t pt-2">
        <div className="md:col-span-4 inline-flex items-center gap-2 font-semibold text-gray-900">
          <Calendar className="w-4 h-4 text-gray-700" />
          {formatDate(booking.booking_date)} · {formatTimeRange(booking.time_slot, booking.duration)}
        </div>
        {location && (
          <div className="md:col-span-3 inline-flex items-center gap-1.5 text-gray-700">
            <MapPin className="w-4 h-4 text-blue-600" />
            <span className="truncate" title={location.address}>{location.name}</span>
          </div>
        )}
        <div className="md:col-span-5">
          <CustomerInfoInline customer={customer} testId={`pending-customer-${refShort}`} />
        </div>
      </div>

      {/* Row 3 — sports + actions inline */}
      <div className="flex items-center justify-between gap-3 flex-wrap border-t pt-2">
        <div className="flex gap-2 flex-wrap">
          <span className="text-[10px] uppercase font-bold text-muted-foreground self-center">
            Courts:
          </span>
          {booking.courts_requested.map((court, idx) => (
            <span key={`${court.sport}-${idx}`} className="sport-badge badge-badminton text-xs">
              {court.sport}
            </span>
          ))}
          {booking.is_expired && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
              <AlertTriangle className="w-3 h-3" /> Expired
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!booking.is_expired ? (
            <AssignCourtsDialog
              booking={booking}
              isOpen={isDialogOpen}
              onOpenChange={setIsDialogOpen}
              onOpenClick={() => onAssignClick(booking)}
              selectedBooking={selectedBooking}
              courtsAssigned={courtsAssigned}
              availableCourts={availableCourts}
              assignedCourtNumbers={assignedCourtNumbers}
              getAvailableCourtNumbersForDropdown={getAvailableCourtNumbersForDropdown}
              onUpdateCourt={onUpdateCourt}
              discountType={discountType}
              setDiscountType={setDiscountType}
              discountValue={discountValue}
              setDiscountValue={setDiscountValue}
              discountReason={discountReason}
              setDiscountReason={setDiscountReason}
              onConfirm={onConfirmAssign}
              compact
            />
          ) : (
            <Button
              size="sm"
              disabled
              className="opacity-50 cursor-not-allowed"
              data-testid="assign-courts-button-disabled"
            >
              Expired
            </Button>
          )}
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onCancelBooking(booking.id)}
            data-testid="cancel-booking-button"
            disabled={cancelling === booking.id}
          >
            <X className="w-3.5 h-3.5 mr-1" />
            {cancelling === booking.id ? 'Cancelling…' : 'Cancel'}
          </Button>
        </div>
      </div>

      {/* Row 4 (only when present) — special requests */}
      {booking.special_requests && (
        <div className="border-t pt-2 text-xs text-gray-700 italic">
          <span className="font-semibold not-italic text-gray-900 mr-1">Note:</span>
          "{booking.special_requests}"
        </div>
      )}
    </Card>
  );
}
